import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import SearchPicker from "@/components/SearchPicker";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 3 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
const CURS = ["TRY", "USD", "EUR", "GBP"];
const CUR_SYM = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

// TR sayı girişi parse: "1.090" → 1090, "381,333" → 381.333, "6,20" → 6.2
// Kural: virgül varsa ondalık ayraç virgüldür (noktalar binlik); virgül yoksa
// son nokta tam 3 hane taşıyorsa binlik, değilse ondalık kabul edilir.
// Geçersiz/boş → NaN döner (çağıran 0'a çevirmez; validasyon yapar).
const parseTrNumber = (s) => {
  if (s === null || s === undefined) return NaN;
  let t = String(s).trim().replace(/\s/g, "");
  if (!t) return NaN;
  if (t.includes(",")) {
    t = t.replace(/\./g, "").replace(",", ".");
  } else {
    const parts = t.split(".");
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3 && parts[0] !== "0")) {
      t = t.replace(/\./g, "");
    }
  }
  const v = Number(t);
  return Number.isFinite(v) ? v : NaN;
};

const EMPTY_FORM = {
  supplier_id: "", quantity: "", unit_price: "", currency: "TRY", tax_rate: "20",
  delivery_date: "", payment_terms: "", quote_no: "", quote_date: "", note: "",
};

export default function PurchaseQuoteDialog({ open, onOpenChange, material, suppliers = [], onChanged }) {
  const [quotes, setQuotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selQty, setSelQty] = useState({}); // {quote_id: raw string}
  const [creating, setCreating] = useState(false);
  const [overConfirmOpen, setOverConfirmOpen] = useState(false);

  const mid = material?.material_id;
  const vid = material?.variant_id || "";
  const kind = material?.kind;
  // Birim: canonical requirement'tan (backend summary), fallback materials_agg satırı
  const unit = material?.manual ? (material?.unit || "adet") : (summary?.unit || material?.unit || "adet");

  const load = async () => {
    if (!mid) return;
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-quotes", {
        params: { material_id: mid, kind, variant_id: vid },
      });
      setQuotes(data.quotes || []);
      setSummary(data.summary || null);
      setSelQty({});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setForm(EMPTY_FORM); setShowForm(false); load(); } }, [open, mid]);

  const remaining = Number(material?.manual ? (material?.total_shortage ?? 0) : (summary?.remaining_qty ?? material?.total_shortage ?? 0));
  const activeQuotes = quotes.filter((q) => q.status === "active");

  const parsedSel = useMemo(() => {
    const m = {};
    for (const q of activeQuotes) {
      const raw = selQty[q.id];
      if (raw === undefined || raw === "") continue;
      m[q.id] = parseTrNumber(raw);
    }
    return m;
  }, [activeQuotes, selQty]);

  const selectedTotal = useMemo(
    () => Object.values(parsedSel).reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0),
    [parsedSel],
  );
  const invalidInput = Object.values(parsedSel).some((v) => !Number.isFinite(v) || v < 0);
  // Fazla alım SERBEST — yalnızca bilgilendirme (hard-block YOK)
  const overAll = selectedTotal - remaining > 1e-6;
  const overQuote = activeQuotes.some((q) => {
    const v = parsedSel[q.id];
    return Number.isFinite(v) && v - Number(q.quantity || 0) > 1e-6;
  });
  const canCreate = !creating && selectedTotal > 0 && !invalidInput;

  const addQuote = async () => {
    if (!form.supplier_id) { toast.error("Tedarikçi seçin"); return; }
    const qty = parseTrNumber(form.quantity);
    const price = parseTrNumber(form.unit_price);
    if (!(qty > 0)) { toast.error(`Geçerli miktar girin (${unit})`); return; }
    if (!(price >= 0)) { toast.error("Geçerli birim fiyat girin"); return; }
    setAdding(true);
    try {
      await api.post("/purchase-quotes", {
        material_id: mid, variant_id: vid, kind,
        request_id: material?.request_id || "",
        supplier_id: form.supplier_id,
        quantity: qty,
        unit_price: price,
        currency: form.currency,
        tax_rate: Number(parseTrNumber(form.tax_rate) || 0),
        delivery_date: form.delivery_date || null,
        payment_terms: form.payment_terms,
        quote_no: form.quote_no,
        quote_date: form.quote_date || null,
        note: form.note,
      });
      toast.success("Teklif kaydedildi");
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const removeQuote = async (qid) => {
    try {
      await api.delete(`/purchase-quotes/${qid}`);
      toast.success("Teklif silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createPOs = async (allowOver = false) => {
    const selections = activeQuotes
      .filter((q) => Number.isFinite(parsedSel[q.id]) && parsedSel[q.id] > 0)
      .map((q) => ({ quote_id: q.id, quantity: parsedSel[q.id] }));
    if (selections.length === 0) { toast.error("En az bir teklif için miktar girin"); return; }
    // Fazla alım / ihtiyaç yok: önce uyarı + onay (backend allow_over ile geçilir)
    if (!allowOver && overAll) { setOverConfirmOpen(true); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/purchase-quotes/create-pos", {
        material_id: mid, variant_id: vid, kind, selections, allow_over: allowOver,
      });
      setOverConfirmOpen(false);
      toast.success(`${data.count} satın alma siparişi oluşturuldu: ${data.created.map((p) => p.po_no).join(", ")}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Tedarikçi Teklifleri — {material?.name}
            {material?.source_sales_quote_no ? ` (Talep: ${material.source_sales_quote_no})` : ""}
          </DialogTitle>
          <DialogDescription>
            {material?.code || "—"} · Teklifleri karşılaştırın, tedarikçi seçin ve satın alma siparişi oluşturun.
          </DialogDescription>
        </DialogHeader>

        {/* Requirement summary */}
        <div className="grid grid-cols-3 gap-2" data-testid="quote-summary">
          <div className="border border-border rounded-sm p-2 text-center">
            <div className="text-[9px] uppercase font-mono text-muted-foreground">İhtiyaç</div>
            <div className="font-mono font-bold" data-testid="quote-summary-req">{fmtQty(material?.manual ? material?.total_shortage : (summary?.requirement_qty ?? material?.total_shortage))} {unit}</div>
          </div>
          <div className="border border-border rounded-sm p-2 text-center">
            <div className="text-[9px] uppercase font-mono text-muted-foreground">Satın Almaya Bağlanan</div>
            <div className="font-mono font-bold text-sky-400" data-testid="quote-summary-linked">{fmtQty(summary?.po_linked_qty)} {unit}</div>
            {Number(summary?.excess_qty) > 0 && (
              <div className="text-[9px] font-mono text-amber-500" data-testid="quote-summary-excess">Fazla Alım: +{fmtQty(summary.excess_qty)} {unit}</div>
            )}
          </div>
          <div className="border border-border rounded-sm p-2 text-center">
            <div className="text-[9px] uppercase font-mono text-muted-foreground">Kalan</div>
            <div className="font-mono font-bold text-rose-500" data-testid="quote-summary-remaining">{fmtQty(remaining)} {unit}</div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            size="sm" variant="outline"
            className="rounded-sm h-7 text-[10px] uppercase font-mono border-emerald-800 text-emerald-400 hover:bg-emerald-950/40"
            onClick={() => setShowForm((v) => !v)}
            data-testid="quote-toggle-form"
          >
            <Plus className="w-3 h-3 mr-1" /> Teklif Ekle
          </Button>
        </div>

        {showForm && (
          <div className="border border-border rounded-sm p-3 space-y-3 bg-muted/20" data-testid="quote-add-form">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase font-mono">Tedarikçi *</Label>
                <SearchPicker
                  items={suppliers}
                  value={form.supplier_id}
                  onChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}
                  getLabel={(s) => s.name}
                  getSubtitle={(s) => [s.tax_no, s.phone].filter(Boolean).join(" · ")}
                  placeholder="Ünvan, vergi no veya telefon ile arayın…"
                  minChars={1}
                  maxSuggestions={10}
                  testId="quote-supplier"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Miktar ({unit}) *</Label>
                <div className="relative">
                  <Input type="text" inputMode="decimal" placeholder={`örn. ${fmtQty(remaining)}`} value={form.quantity} data-testid="quote-qty"
                    onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} className="pr-10" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground">{unit}</span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Birim Fiyat *</Label>
                <div className="relative">
                  <Input type="text" inputMode="decimal" placeholder="örn. 235,00" value={form.unit_price} data-testid="quote-price"
                    onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))} className="pr-12" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground">{CUR_SYM[form.currency]} / {unit}</span>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                  <SelectTrigger data-testid="quote-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURS.map((c) => <SelectItem key={c} value={c} data-testid={`quote-currency-${c}`}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">KDV %</Label>
                <Input type="text" inputMode="decimal" value={form.tax_rate} data-testid="quote-tax"
                  onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Termin / Teslim</Label>
                <Input type="date" value={form.delivery_date} data-testid="quote-delivery"
                  onChange={(e) => setForm((f) => ({ ...f, delivery_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Ödeme Koşulu</Label>
                <Input placeholder="örn. 30 gün" value={form.payment_terms} data-testid="quote-payment"
                  onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Teklif No (ops.)</Label>
                <Input value={form.quote_no} data-testid="quote-no"
                  onChange={(e) => setForm((f) => ({ ...f, quote_no: e.target.value }))} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-mono">Teklif Tarihi</Label>
                <Input type="date" value={form.quote_date} data-testid="quote-date"
                  onChange={(e) => setForm((f) => ({ ...f, quote_date: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase font-mono">Not</Label>
                <Textarea rows={1} value={form.note} data-testid="quote-note"
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={addQuote} disabled={adding}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-500" data-testid="quote-submit">
                {adding && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Kaydet
              </Button>
            </div>
          </div>
        )}

        {/* Compare table */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : quotes.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8 border border-dashed border-border rounded-sm" data-testid="quote-empty">
            Henüz teklif girilmedi. "Teklif Ekle" ile başlayın.
          </div>
        ) : (
          <div className="border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-[10px] uppercase font-mono text-muted-foreground">
                  <th className="text-left px-3 py-2">Tedarikçi</th>
                  <th className="text-right px-3 py-2">Miktar</th>
                  <th className="text-right px-3 py-2">Birim Fiyat</th>
                  <th className="text-right px-3 py-2">KDV</th>
                  <th className="text-left px-3 py-2">Termin</th>
                  <th className="text-left px-3 py-2">Ödeme</th>
                  <th className="text-right px-3 py-2">Toplam</th>
                  <th className="text-right px-3 py-2">Seçim ({unit})</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {quotes.map((q) => {
                  const converted = q.status !== "active";
                  const sv = parsedSel[q.id];
                  const selOver = !converted && Number.isFinite(sv) && sv - Number(q.quantity || 0) > 1e-6;
                  return (
                    <tr key={q.id} className={converted ? "opacity-60" : ""} data-testid={`quote-row-${q.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{q.supplier_name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {q.quote_no ? `Ref: ${q.quote_no} · ` : ""}{fmtDate(q.quote_date || q.created_at)}
                        </div>
                        {converted && (
                          <span className="text-[9px] uppercase font-mono border border-sky-500/50 text-sky-300 rounded-sm px-1 py-0.5">
                            PO: {q.po_no || "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtQty(q.quantity)} {unit}</td>
                      <td className="px-3 py-2 text-right font-mono">{CUR_SYM[q.currency] || ""}{fmt(q.unit_price)} / {unit}</td>
                      <td className="px-3 py-2 text-right font-mono">%{Number(q.tax_rate || 0)}</td>
                      <td className="px-3 py-2">{fmtDate(q.delivery_date)}</td>
                      <td className="px-3 py-2">{q.payment_terms || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">
                        {CUR_SYM[q.currency] || ""}{fmt(q.quantity * q.unit_price)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!converted && (
                          <div className="relative w-28 ml-auto">
                            <Input
                              type="text" inputMode="decimal"
                              className={`h-7 text-right font-mono pr-9 bg-background border-border ${selOver ? "border-amber-500" : ""}`}
                              value={selQty[q.id] ?? ""}
                              placeholder="0"
                              title={`Bu tedarikçiden alınacak miktar — maks: ${fmtQty(Math.min(Number(q.quantity || 0), remaining))} ${unit}`}
                              onChange={(e) => setSelQty((s) => ({ ...s, [q.id]: e.target.value }))}
                              data-testid={`quote-select-qty-${q.id}`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-mono text-muted-foreground pointer-events-none">{unit}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {!converted && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-400"
                            onClick={() => removeQuote(q.id)} data-testid={`quote-delete-${q.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="items-center gap-3">
          <div className="mr-auto text-xs font-mono" data-testid="quote-selection-status">
            {invalidInput ? (
              <span className="text-rose-400 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Geçersiz miktar girişi
              </span>
            ) : (
              <span className="text-muted-foreground">
                Seçilen: <b className="text-foreground">{fmtQty(selectedTotal)}</b> / Kalan: {fmtQty(remaining)} {unit}
                {overAll && (
                  <span className="text-amber-500 ml-2" data-testid="quote-excess-warning">
                    · İhtiyaç Fazlası: +{fmtQty(selectedTotal - remaining)} {unit}
                  </span>
                )}
                {overQuote && (
                  <span className="text-amber-500 ml-2" data-testid="quote-overquote-warning">
                    · Sipariş miktarı tedarikçinin teklif miktarını aşıyor
                  </span>
                )}
              </span>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Kapat</Button>
          <Button
            onClick={() => createPOs(false)}
            disabled={!canCreate}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
            data-testid="quote-create-pos"
          >
            {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Satın Alma Siparişi Oluştur
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Fazla alım / ihtiyaç yok — uyarı + onay */}
      <Dialog open={overConfirmOpen} onOpenChange={setOverConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="quote-over-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Fazla Alım Uyarısı
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2" data-testid="quote-over-confirm-message">
            {remaining > 1e-6 ? (
              <>
                <p>
                  Kalan ihtiyaç <b className="font-mono">{fmtQty(remaining)} {unit}</b>.
                  {" "}<b className="font-mono">{fmtQty(selectedTotal)} {unit}</b> satın alıyorsunuz.
                </p>
                <p className="text-muted-foreground">
                  <b className="font-mono text-foreground">{fmtQty(remaining)} {unit}</b> mevcut ihtiyaca,
                  fazla <b className="font-mono text-amber-500">{fmtQty(selectedTotal - remaining)} {unit}</b> genel stoğa alınacaktır.
                </p>
              </>
            ) : (
              <p>
                Bu ürün için açık ihtiyaç bulunmuyor. Girilen miktarın tamamı
                {" "}(<b className="font-mono">{fmtQty(selectedTotal)} {unit}</b>) stok alımı olacaktır.
              </p>
            )}
            <p>Devam etmek istiyor musunuz?</p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOverConfirmOpen(false)}
              data-testid="quote-over-confirm-cancel"
            >
              Vazgeç
            </Button>
            <Button
              onClick={() => createPOs(true)}
              disabled={creating}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40"
              data-testid="quote-over-confirm-accept"
            >
              {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Devam Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
